import React, {useEffect, useState, useMemo} from 'react';
import {
    MapPin,
    TrendingUp,
    Shield,
    Users,
    Building,
    Zap,
    Star,
    Eye,
    Search,
    CheckCircle,
    Filter,
    SortAsc,
    Heart,
    Share2,
    BarChart3,
    Car,
    ShoppingCart,
    GraduationCap,
    Hospital,
    Utensils,
    Wifi,
    Trees,
    Home,
    DollarSign,
    Clock,
    Phone,
    Mail,
    Calendar,
    ChevronDown,
    X,
    Info,
    Navigation,
    Bookmark,
    Grid3X3,
    List,
    ArrowUpDown, ChevronLeft, ChevronRight, ArrowLeft
} from 'lucide-react';
import {API_CONSTANTS} from "../../Constants";

const ScoreBadge = ({label, score, icon: Icon}) => (
    <div className="flex items-center gap-2 text-sm text-soil-600 bg-soil-50 px-3 py-2 rounded-lg">
        <Icon className="w-4 h-4 text-highland-500"/>
        <span>{label}: <strong className="text-highland-500 ml-1">{score}/10</strong></span>
    </div>
);

// Parse markdown description
const parseMarkdownDescription = (description) => {
    const lines = description.split('\n');
    const sections = {};
    let currentSection = '';
    let currentContent = '';

    lines.forEach(line => {
        if (line.startsWith('#### ')) {
            if (currentSection) {
                sections[currentSection] = currentContent.trim();
            }
            currentSection = line.replace('#### ', '').replace(/^\d+\)\s*/, '');
            currentContent = '';
        } else if (line.startsWith('### ')) {
            sections.title = line.replace('### ', '');
        } else if (line.trim() && !line.startsWith('#')) {
            currentContent += line + ' ';
        }
    });

    if (currentSection) {
        sections[currentSection] = currentContent.trim();
    }

    return sections;
};

const PropertyPlaceholder = ({className = "w-full h-full"}) => (
    <div
        className={`${className} bg-gradient-to-br from-karibu-200 to-highland-50 rounded-lg flex flex-col items-center justify-center border border-highland-100`}>
        <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-3 shadow-md">
            <MapPin className="w-7 h-7 text-highland-500"/>
        </div>
        <p className="text-highland-600 font-bold">COMING SOON</p>
        <p className="text-highland-500 text-sm mt-1">Neighborhood Gallery</p>
    </div>
);

const NeighborhoodCard = ({neighborhood, viewMode, onFavorite, onShare, onViewDetails}) => {
    const [isFavorited, setIsFavorited] = useState(false);

    const handleFavorite = () => {
        setIsFavorited(!isFavorited);
        onFavorite?.(neighborhood.id);
    };

    if (viewMode === 'list') {
        return (
            <div
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-soil-100">
                <div className="flex">
                    <div className="w-48 h-32 relative">
                        {neighborhood.image !== '' ? (
                            <img
                                src={neighborhood.image || ''}
                                alt={neighborhood.name}
                                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                        ) : (
                            <>
                                <PropertyPlaceholder/>
                            </>
                        )}
                        {neighborhood.trending && (
                            <div
                                className="absolute top-2 left-2 bg-murram-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                                Trending
                            </div>
                        )}
                        {neighborhood.featured && (
                            <div
                                className="absolute top-2 right-2 bg-dawn-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                                Featured
                            </div>
                        )}
                    </div>

                    <div className="flex-1 p-6">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h3 className="font-display text-xl font-semibold text-soil-900">{neighborhood.name}</h3>
                                <p className="text-lg font-medium text-highland-500">{neighborhood.price_range || neighborhood.city}</p>
                                <p className="text-sm text-soil-600 mt-1">{parseMarkdownDescription(neighborhood.description)['Overview and Character']?.substring(0, 100)}...</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleFavorite}
                                    className={`p-2 rounded-full transition-colors ${
                                        isFavorited ? 'bg-murram-100 text-murram-600' : 'bg-soil-100 text-soil-600 hover:bg-soil-100'
                                    }`}
                                >
                                    <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`}/>
                                </button>
                                <button
                                    onClick={() => onShare?.(neighborhood)}
                                    className="p-2 rounded-full bg-soil-100 text-soil-600 hover:bg-soil-100 transition-colors"
                                >
                                    <Share2 className="w-4 h-4"/>
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            <ScoreBadge label="Lifestyle" score={neighborhood.lifestyle_score || 0} icon={Star}/>
                            <ScoreBadge label="Safety" score={neighborhood.safety_score || 0} icon={Shield}/>
                            <ScoreBadge label="Transport" score={neighborhood.transport_score || 0} icon={Car}/>
                            <ScoreBadge label="Amenities" score={neighborhood.family_friendliness_score || 0}
                                        icon={Building}/>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 text-sm text-soil-600">
                                <span className="flex items-center gap-1">
                                    <Home className="w-4 h-4"/>
                                    {neighborhood.total_properties || 0} properties
                                </span>
                                <span className="flex items-center gap-1">
                                    <Clock className="w-4 h-4"/>
                                    {neighborhood.avg_commute_time || 'N/A'} min commute
                                </span>
                            </div>
                            <button
                                onClick={() => onViewDetails?.(neighborhood)}
                                className="bg-highland-500 text-white px-4 py-2 rounded-lg hover:bg-highland-600 transition-colors text-sm font-medium"
                            >
                                View Details
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }


    return (
        <div
            className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 border border-soil-100 overflow-hidden group">
            <div className="relative">
                {neighborhood.image !== '' ? (
                    <img
                        src={neighborhood.image || ''}
                        alt={neighborhood.name}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                ) : (
                    <>
                        <PropertyPlaceholder/>
                    </>
                )}
                <div className="absolute top-3 left-3 flex gap-2">
                    {neighborhood.trending && (
                        <div className="bg-murram-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                            Trending
                        </div>
                    )}
                    {neighborhood.featured && (
                        <div className="bg-dawn-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                            Featured
                        </div>
                    )}
                </div>
                <div className="absolute top-3 right-3 flex gap-2">
                    <button
                        onClick={handleFavorite}
                        className={`p-2 rounded-full backdrop-blur-sm transition-colors ${
                            isFavorited ? 'bg-murram-100/90 text-murram-600' : 'bg-white/90 text-soil-600 hover:bg-white'
                        }`}
                    >
                        <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`}/>
                    </button>
                    <button
                        onClick={() => onShare?.(neighborhood)}
                        className="p-2 rounded-full bg-white/90 text-soil-600 hover:bg-white transition-colors backdrop-blur-sm"
                    >
                        <Share2 className="w-4 h-4"/>
                    </button>
                </div>
            </div>

            <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <h3 className="text-xl font-semibold text-soil-900">{neighborhood.name}</h3>
                        {/*<p className="text-lg font-medium text-highland-500">{neighborhood.price_range || 'N/A'}</p>*/}
                        <p className="text-lg font-medium text-highland-500">{neighborhood.city}</p>
                    </div>
                    <span className="px-3 py-1 bg-highland-50 text-highland-600 text-sm rounded-full">
                        {neighborhood.best_for?.join(', ') || 'N/A'}
                    </span>
                </div>

                <p className="text-soil-600 text-sm mb-4">{parseMarkdownDescription(neighborhood.description)['Overview and Character']?.substring(0, 100)}...</p>

                <div className="flex flex-wrap gap-2 mb-4">
                    {neighborhood.characteristics?.map((char, idx) => (
                        <span
                            key={idx}
                            className="px-3 py-1 bg-soil-100 text-soil-600 text-sm rounded-full"
                        >
                            {char}
                        </span>
                    )) || <span className="text-soil-600 text-sm">No characteristics available</span>}
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4">
                    <ScoreBadge label="Lifestyle" score={neighborhood.lifestyle_score || 0} icon={Star}/>
                    <ScoreBadge label="Safety" score={neighborhood.safety_score || 0} icon={Shield}/>
                    <ScoreBadge label="Transport" score={neighborhood.transport_score || 0} icon={Car}/>
                    <ScoreBadge label="Amenities" score={neighborhood.family_friendliness_score || 0} icon={Building}/>
                </div>

                <div className="flex items-center justify-between text-sm text-soil-600 mb-4">
                    <span className="flex items-center gap-1">
                        <Home className="w-4 h-4"/>
                        {neighborhood.total_properties || 0} properties
                    </span>
                    <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4"/>
                        {neighborhood.avg_commute_time || 'N/A'} min
                    </span>
                </div>

                <button
                    onClick={() => onViewDetails?.(neighborhood)}
                    className="w-full bg-highland-500 text-white py-2 rounded-lg hover:bg-highland-600 transition-colors font-medium"
                >
                    View Full Details
                </button>
            </div>
        </div>
    );
};

const FilterPanel = ({filters, onFilterChange, onClose}) => {
    const priceRanges = [
        {label: 'Under KES 30,000', value: 'under_30k'},
        {label: 'KES 30,000 - 60,000', value: '30k_60k'},
        {label: 'KES 60,000 - 100,000', value: '60k_100k'},
        {label: 'Over KES 100,000', value: 'over_100k'}
    ];

    const bestForOptions = [
        'Young professionals',
        'Families',
        'Students',
        'Nature Lovers',
        'Peaceful Living Seekers',
        'Individuals seeking a quiet neighborhood',
        'Peaceful Living Enthusiasts'
    ];

    return (
        <div className="bg-white rounded-xl shadow-lg border border-soil-100 p-6">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-soil-900">Filters</h3>
                <button
                    onClick={onClose}
                    className="p-2 hover:bg-soil-100 rounded-lg transition-colors"
                >
                    <X className="w-5 h-5 text-soil-600"/>
                </button>
            </div>

            <div className="space-y-6">
                {/* Price Range */}
                <div>
                    <h4 className="font-medium text-soil-900 mb-3">Price Range</h4>
                    <div className="space-y-2">
                        {priceRanges.map((range) => (
                            <label key={range.value} className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={filters.priceRange?.includes(range.value)}
                                    onChange={(e) => {
                                        const current = filters.priceRange || [];
                                        const updated = e.target.checked
                                            ? [...current, range.value]
                                            : current.filter(v => v !== range.value);
                                        onFilterChange({...filters, priceRange: updated});
                                    }}
                                    className="rounded border-soil-100 text-highland-500 focus:ring-highland-500"
                                />
                                <span className="text-sm text-soil-600">{range.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Best For */}
                <div>
                    <h4 className="font-medium text-soil-900 mb-3">Best For</h4>
                    <div className="space-y-2">
                        {bestForOptions.map((option) => (
                            <label key={option} className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={filters.bestFor?.includes(option)}
                                    onChange={(e) => {
                                        const current = filters.bestFor || [];
                                        const updated = e.target.checked
                                            ? [...current, option]
                                            : current.filter(v => v !== option);
                                        onFilterChange({...filters, bestFor: updated});
                                    }}
                                    className="rounded border-soil-100 text-highland-500 focus:ring-highland-500"
                                />
                                <span className="text-sm text-soil-600">{option}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Minimum Scores */}
                <div>
                    <h4 className="font-medium text-soil-900 mb-3">Minimum Scores</h4>
                    <div className="space-y-3">
                        {[
                            {key: 'lifestyle', label: 'Lifestyle', icon: Star},
                            {key: 'safety', label: 'Safety', icon: Shield},
                            {key: 'transport', label: 'Transport', icon: Car},
                            {key: 'amenities', label: 'Amenities', icon: Building}
                        ].map(({key, label, icon: Icon}) => (
                            <div key={key}>
                                <label className="flex items-center gap-2 mb-2">
                                    <Icon className="w-4 h-4 text-soil-400"/>
                                    <span className="text-sm text-soil-600">{label}</span>
                                </label>
                                <input
                                    type="range"
                                    min="1"
                                    max="10"
                                    value={filters.minScores?.[key] || 1}
                                    onChange={(e) => {
                                        const minScores = {...filters.minScores};
                                        minScores[key] = parseInt(e.target.value);
                                        onFilterChange({...filters, minScores});
                                    }}
                                    className="w-full"
                                />
                                <div className="flex justify-between text-xs text-soil-400 mt-1">
                                    <span>1</span>
                                    <span>{filters.minScores?.[key] || 1}</span>
                                    <span>10</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Special Features */}
                <div>
                    <h4 className="font-medium text-soil-900 mb-3">Special Features</h4>
                    <div className="space-y-2">
                        {[
                            {key: 'trending', label: 'Trending neighborhoods'},
                            {key: 'featured', label: 'Featured properties'},
                            {key: 'lowCrime', label: 'Low crime areas'},
                            {key: 'walkable', label: 'Highly walkable'}
                        ].map(({key, label}) => (
                            <label key={key} className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={filters.features?.[key]}
                                    onChange={(e) => {
                                        const features = {...filters.features};
                                        features[key] = e.target.checked;
                                        onFilterChange({...filters, features});
                                    }}
                                    className="rounded border-soil-100 text-highland-500 focus:ring-highland-500"
                                />
                                <span className="text-sm text-soil-600">{label}</span>
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mt-6 pt-6 border-t border-soil-100">
                <button
                    onClick={() => onFilterChange({})}
                    className="w-full bg-soil-100 text-soil-600 py-2 rounded-lg hover:bg-soil-100 transition-colors"
                >
                    Clear All Filters
                </button>
            </div>
        </div>
    );
};

const NeighborhoodsPage = () => {
    const [neighborhoods, setNeighborhoods] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({});
    const [sortBy, setSortBy] = useState('name');
    const [viewMode, setViewMode] = useState('grid');
    const [showFilters, setShowFilters] = useState(false);
    const [selectedNeighborhood, setSelectedNeighborhood] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const getSectionIcon = (sectionName) => {
        switch (sectionName.toLowerCase()) {
            case 'overview and character':
                return <MapPin className="w-4 h-4 text-highland-500"/>;
            case 'best suited for (demographics)':
                return <Users className="w-4 h-4 text-highland-600"/>;
            case 'average rent ranges':
                return <DollarSign className="w-4 h-4 text-dawn-600"/>;
            case 'transportation':
                return <Car className="w-4 h-4 text-jacaranda-500"/>;
            case 'local amenities':
                return <Home className="w-4 h-4 text-jacaranda-600"/>;
            case 'safety overview':
                return <Shield className="w-4 h-4 text-murram-600"/>;
            case 'notable landmarks':
                return <Star className="w-4 h-4 text-dawn-500"/>;
            default:
                return <MapPin className="w-4 h-4 text-soil-600"/>;
        }
    };


    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        totalNeighborhoods: 0,
        totalPages: 0
    });

    const fetchNeighborhoods = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const token = localStorage.getItem('authToken');

            const queryParams = new URLSearchParams({
                page: pagination.page.toString(),
                limit: pagination.limit.toString(),
                sortBy: 'date_created',
                sortOrder: 'desc',
            });

            const response = await fetch(
                `${API_CONSTANTS.BASE_URL}/neighborhoods?${queryParams}`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.message || 'Failed to fetch neighborhoods');
            }

            // Transform the API response to match the expected structure
            const transformedNeighborhoods = data.data.neighborhoods.map(n => {
                const metadata = JSON.parse(n.neighborhoodMetadata);
                const aiInsights = JSON.parse(metadata.ai_insights || '{}');
                return {
                    id: n.neighborhoodId,
                    name: n.name,
                    city: n.city,
                    image: metadata.image || '',
                    price_range: metadata.price_range || n.averageRent || 'N/A',
                    trending: metadata.trending || false,
                    featured: metadata.featured || false,
                    description: metadata.description || 'No description available',
                    lifestyle_score: aiInsights.lifestyle_score || 0,
                    safety_score: aiInsights.safety_score || 0,
                    transport_score: aiInsights.transport_score || 0,
                    amenities_score: aiInsights.family_friendliness_score || 0,
                    total_properties: n.propertyCount || 0,
                    avg_commute_time: metadata.avg_commute_time || 'N/A',
                    best_for: aiInsights.best_for || [],
                    characteristics: aiInsights.popular_amenities || [],
                    walkability: aiInsights.walkability || 0,
                    crime_rate: aiInsights.crime_rate || 'Unknown'
                };
            });

            setNeighborhoods(transformedNeighborhoods);
            setPagination(prev => ({
                ...prev,
                page: data.data.metadata.currentPage || 1,
                limit: data.data.metadata.limit || 10,
                totalNeighborhoods: data.data.metadata.totalCount || 0,
                totalPages: data.data.metadata.totalPages || 0,
            }));
        } catch (err) {
            console.error('Error fetching neighborhoods:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchNeighborhoods();
    }, [pagination.page, pagination.limit]);

    // Pagination handlers
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({
                ...prev,
                page: newPage
            }));
        }
    };

    const handleLimitChange = (newLimit) => {
        setPagination(prev => ({
            ...prev,
            limit: parseInt(newLimit),
            page: 1 // Reset to first page when changing limit
        }));
    };

    const filteredAndSortedNeighborhoods = useMemo(() => {
        let filtered = neighborhoods.filter(n => {
            // Search filter
            if (searchTerm && !n.name.toLowerCase().includes(searchTerm.toLowerCase())) {
                return false;
            }

            // Price range filter
            if (filters.priceRange?.length > 0) {
                const hasMatchingPrice = filters.priceRange.some(range => {
                    const avgPrice = parseFloat(n.price_range.replace(/[^0-9.-]+/g, '')) || 0;
                    switch (range) {
                        case 'under_30k':
                            return avgPrice < 30000;
                        case '30k_60k':
                            return avgPrice >= 30000 && avgPrice < 60000;
                        case '60k_100k':
                            return avgPrice >= 60000 && avgPrice < 100000;
                        case 'over_100k':
                            return avgPrice >= 100000;
                        default:
                            return true;
                    }
                });
                if (!hasMatchingPrice) return false;
            }

            // Best for filter
            if (filters.bestFor?.length > 0) {
                if (!n.best_for.some(bf => filters.bestFor.includes(bf))) return false;
            }

            // Minimum scores filter
            if (filters.minScores) {
                if (filters.minScores.lifestyle && n.lifestyle_score < filters.minScores.lifestyle) return false;
                if (filters.minScores.safety && n.safety_score < filters.minScores.safety) return false;
                if (filters.minScores.transport && n.transport_score < filters.minScores.transport) return false;
                if (filters.minScores.amenities && n.amenities_score < filters.minScores.amenities) return false;
            }

            // Special features filter
            if (filters.features) {
                if (filters.features.trending && !n.trending) return false;
                if (filters.features.featured && !n.featured) return false;
                if (filters.features.lowCrime && !['Very Low', 'Low'].includes(n.crime_rate)) return false;
                if (filters.features.walkable && n.walkability < 7) return false;
            }

            return true;
        });

        // Sort
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'price_asc':
                    return (parseFloat(a.price_range.replace(/[^0-9.-]+/g, '')) || 0) - (parseFloat(b.price_range.replace(/[^0-9.-]+/g, '')) || 0);
                case 'price_desc':
                    return (parseFloat(b.price_range.replace(/[^0-9.-]+/g, '')) || 0) - (parseFloat(a.price_range.replace(/[^0-9.-]+/g, '')) || 0);
                case 'lifestyle':
                    return b.lifestyle_score - a.lifestyle_score;
                case 'safety':
                    return b.safety_score - a.safety_score;
                case 'transport':
                    return b.transport_score - a.transport_score;
                case 'trending':
                    return (b.trending ? 1 : 0) - (a.trending ? 1 : 0);
                default:
                    return 0;
            }
        });

        return filtered;
    }, [neighborhoods, searchTerm, filters, sortBy]);

    const handleShare = (neighborhood) => {
        if (navigator.share) {
            navigator.share({
                title: `${neighborhood.name} - Neighborhood Info`,
                text: parseMarkdownDescription(neighborhood.description)['Overview and Character'],
                url: window.location.href
            });
        } else {
            // Fallback - copy to clipboard
            navigator.clipboard.writeText(
                `Check out ${neighborhood.name}: ${parseMarkdownDescription(neighborhood.description)['Overview and Character']} - ${window.location.href}`
            );
        }
    };

    const handleViewDetails = (neighborhood) => {
        setSelectedNeighborhood(neighborhood);
    };

    const activeFiltersCount = Object.values(filters).filter(filter => {
        if (Array.isArray(filter)) return filter.length > 0;
        if (typeof filter === 'object') return Object.values(filter).some(Boolean);
        return Boolean(filter);
    }).length;

    if (error) {
        return (
            <div className="min-h-screen bg-karibu-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-murram-600 mb-4">Error: {error}</div>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-highland-500 text-white px-4 py-2 rounded hover:bg-highland-600"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-karibu-100">
            <div style={{paddingTop: '5rem'}}></div>

            {/* Header */}
            <div className="relative bg-highland-700 text-karibu-100 overflow-hidden">
                <div
                    aria-hidden="true"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[420px] rounded-arch bg-gradient-to-b from-dawn-500/20 via-highland-500/10 to-transparent blur-2xl"
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-4 sm:mb-6">
                            <div className="p-3 sm:p-4 bg-white/10 backdrop-blur-sm rounded-2xl">
                                <MapPin className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12"/>
                            </div>
                        </div>
                        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 px-2">
                            Discover Your Perfect
                            <span className="block text-dawn-400">
                                Neighborhood
                            </span>
                        </h1>
                        <p className="text-base sm:text-lg lg:text-xl text-karibu-200/80 max-w-3xl mx-auto mb-6 sm:mb-8 px-4">
                            Compare neighborhoods, analyze market trends, and find your ideal home with our
                            comprehensive data-driven platform.
                        </p>

                        {/* Stats */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-4xl mx-auto px-4">
                            <div className="text-center">
                                <div className="font-mono text-2xl sm:text-3xl font-bold text-karibu-100 mb-1">1,200+</div>
                                <div className="text-karibu-200/70 text-xs sm:text-sm">Properties Listed</div>
                            </div>
                            <div className="text-center">
                                <div className="font-mono text-2xl sm:text-3xl font-bold text-karibu-100 mb-1">25+</div>
                                <div className="text-karibu-200/70 text-xs sm:text-sm">Neighborhoods</div>
                            </div>
                            <div className="text-center">
                                <div className="font-mono text-2xl sm:text-3xl font-bold text-karibu-100 mb-1">98%</div>
                                <div className="text-karibu-200/70 text-xs sm:text-sm">Satisfaction Rate</div>
                            </div>
                            <div className="text-center">
                                <div className="font-mono text-2xl sm:text-3xl font-bold text-karibu-100 mb-1">24/7</div>
                                <div className="text-karibu-200/70 text-xs sm:text-sm">Support</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center space-x-2 text-sm text-soil-600">
                    <button
                        onClick={() => window.history.back()}
                        className="flex items-center hover:text-highland-500 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1"/>
                        Back
                    </button>
                    <span>/</span>
                    <button
                        onClick={() => window.location.href = '/'}
                        className="flex items-center hover:text-highland-500 transition-colors"
                    >
                        <Home className="w-4 h-4 mr-1"/>
                        Home
                    </button>
                    <span>/</span>
                    <span className="text-soil-900">Neighborhoods</span>
                </div>
            </div>

            {/* Search and Controls */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-soil-100 p-4 sm:p-6">
                    <div className="flex flex-col gap-4">
                        {/* Search */}
                        <div className="relative">
                            <Search
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-soil-400"/>
                            <input
                                type="text"
                                placeholder="Search neighborhoods..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent text-sm sm:text-base"
                            />
                        </div>

                        {/* Controls */}
                        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                            {/* Sort */}
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="flex-1 sm:flex-none px-4 py-3 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent text-sm"
                            >
                                <option value="name">Sort by Name</option>
                                <option value="price_asc">Price: Low to High</option>
                                <option value="price_desc">Price: High to Low</option>
                                <option value="lifestyle">Lifestyle Score</option>
                                <option value="safety">Safety Score</option>
                                <option value="transport">Transport Score</option>
                                <option value="trending">Trending First</option>
                            </select>

                            {/* Items per page */}
                            <select
                                value={pagination.limit}
                                onChange={(e) => handleLimitChange(e.target.value)}
                                className="flex-1 sm:flex-none px-4 py-3 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent text-sm"
                            >
                                <option value="5">5 per page</option>
                                <option value="10">10 per page</option>
                                <option value="20">20 per page</option>
                                <option value="50">50 per page</option>
                            </select>

                            <div className="flex justify-between sm:justify-start gap-3">
                                {/* View Mode */}
                                <div className="flex bg-soil-100 rounded-lg p-1">
                                    <button
                                        onClick={() => setViewMode('grid')}
                                        className={`p-2 rounded-md transition-colors ${
                                            viewMode === 'grid'
                                                ? 'bg-white shadow-sm text-highland-500'
                                                : 'text-soil-600 hover:text-soil-900'
                                        }`}
                                    >
                                        <Grid3X3 className="w-5 h-5"/>
                                    </button>
                                    <button
                                        onClick={() => setViewMode('list')}
                                        className={`p-2 rounded-md transition-colors ${
                                            viewMode === 'list'
                                                ? 'bg-white shadow-sm text-highland-500'
                                                : 'text-soil-600 hover:text-soil-900'
                                        }`}
                                    >
                                        <List className="w-5 h-5"/>
                                    </button>
                                </div>

                                {/* Filters */}
                                <button
                                    onClick={() => setShowFilters(!showFilters)}
                                    className={`flex items-center gap-2 px-4 py-2 sm:py-3 rounded-lg transition-colors text-sm ${
                                        showFilters || activeFiltersCount > 0
                                            ? 'bg-highland-500 text-white'
                                            : 'bg-soil-100 text-soil-600 hover:bg-soil-100'
                                    }`}
                                >
                                    <Filter className="w-4 h-4 sm:w-5 sm:h-5"/>
                                    <span className="hidden sm:inline">Filters</span>
                                    {activeFiltersCount > 0 && (
                                        <span className="bg-white/20 text-xs px-2 py-1 rounded-full">
                                            {activeFiltersCount}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Results Info */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-6 mb-6 px-2">
                    <div className="text-soil-600 text-sm sm:text-base">
                        <span
                            className="font-medium text-soil-900">{filteredAndSortedNeighborhoods.length}</span> neighborhoods
                        found
                        {searchTerm && (<span> for "{searchTerm}"</span>)}
                        {isLoading && <span className="ml-2 text-highland-500">Loading...</span>}
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-soil-600">
                        <Eye className="w-4 h-4"/>
                        Last updated: Today
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
                    {/* Filters Panel */}
                    {showFilters && (
                        <>
                            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
                                 onClick={() => setShowFilters(false)}/>
                            <div
                                className={`${showFilters ? 'block' : 'hidden'} fixed inset-y-0 right-0 w-80 max-w-[85vw] bg-white shadow-2xl z-50 overflow-y-auto lg:relative lg:inset-y-auto lg:w-80 lg:flex-shrink-0 lg:shadow-none lg:z-auto`}>
                                <FilterPanel
                                    filters={filters}
                                    onFilterChange={setFilters}
                                    onClose={() => setShowFilters(false)}
                                />
                            </div>
                        </>
                    )}

                    {/* Neighborhoods Grid/List */}
                    <div className="flex-1 min-w-0">
                        {filteredAndSortedNeighborhoods.length === 0 ? (
                            <div className="text-center py-12 px-4">
                                <div
                                    className="w-16 h-16 mx-auto mb-4 bg-soil-100 rounded-full flex items-center justify-center">
                                    <MapPin className="w-8 h-8 text-soil-400"/>
                                </div>
                                <h3 className="text-lg sm:text-xl font-medium text-soil-900 mb-2">No neighborhoods
                                    found</h3>
                                <p className="text-soil-600 mb-4 text-sm sm:text-base">Try adjusting your search or
                                    filters</p>
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setFilters({});
                                    }}
                                    className="text-highland-500 hover:text-highland-600 font-medium text-sm sm:text-base"
                                >
                                    Clear all filters
                                </button>
                            </div>
                        ) : (
                            <div
                                className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6' : 'space-y-4'}>
                                {filteredAndSortedNeighborhoods.map(neighborhood => (
                                    <NeighborhoodCard
                                        key={neighborhood.id}
                                        neighborhood={neighborhood}
                                        viewMode={viewMode}
                                        onFavorite={() => {
                                        }}
                                        onShare={handleShare}
                                        onViewDetails={handleViewDetails}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="flex items-center justify-between mt-8 px-4">
                                <div className="text-sm text-soil-600">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                                    {Math.min(pagination.page * pagination.limit, pagination.totalNeighborhoods)} of{' '}
                                    {pagination.totalNeighborhoods} neighborhoods
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handlePageChange(pagination.page - 1)}
                                        disabled={pagination.page === 1}
                                        className="p-2 rounded-lg border border-soil-100 hover:bg-soil-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeft className="w-4 h-4"/>
                                    </button>

                                    <div className="flex items-center gap-1">
                                        {[...Array(pagination.totalPages)].map((_, i) => {
                                            const page = i + 1;
                                            const isCurrentPage = page === pagination.page;
                                            const isNearCurrentPage = Math.abs(page - pagination.page) <= 2;
                                            const isFirstOrLast = page === 1 || page === pagination.totalPages;

                                            if (isCurrentPage || isNearCurrentPage || isFirstOrLast) {
                                                return (
                                                    <button
                                                        key={page}
                                                        onClick={() => handlePageChange(page)}
                                                        className={`px-3 py-1 rounded-md text-sm ${
                                                            isCurrentPage
                                                                ? 'bg-highland-500 text-white'
                                                                : 'text-soil-600 hover:bg-soil-100'
                                                        }`}
                                                    >
                                                        {page}
                                                    </button>
                                                );
                                            } else if (page === 2 && pagination.page > 4) {
                                                return <span key="start-ellipsis"
                                                             className="px-2 text-soil-400">...</span>;
                                            } else if (page === pagination.totalPages - 1 && pagination.page < pagination.totalPages - 3) {
                                                return <span key="end-ellipsis"
                                                             className="px-2 text-soil-400">...</span>;
                                            }
                                            return null;
                                        })}
                                    </div>

                                    <button
                                        onClick={() => handlePageChange(pagination.page + 1)}
                                        disabled={pagination.page === pagination.totalPages}
                                        className="p-2 rounded-lg border border-soil-100 hover:bg-soil-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronRight className="w-4 h-4"/>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Detailed View Modal */}
            {selectedNeighborhood && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div
                        className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className="relative">
                            <div
                                className="w-full h-48 sm:h-64 bg-soil-100 rounded-t-xl sm:rounded-t-2xl flex items-center justify-center">

                                {selectedNeighborhood.image !== '' ? (
                                    <img
                                        src={selectedNeighborhood.image || ''}
                                        alt={selectedNeighborhood.name}
                                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                                    />
                                ) : (
                                    <>
                                        <PropertyPlaceholder/>
                                    </>
                                )}
                            </div>
                            <button
                                onClick={() => setSelectedNeighborhood(null)}
                                className="absolute top-3 sm:top-4 right-3 sm:right-4 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors"
                            >
                                <X className="w-5 h-5"/>
                            </button>
                        </div>

                        <div className="p-4 sm:p-6 lg:p-8">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                                <div className="flex-1">
                                    <h2 className="font-display text-2xl sm:text-3xl font-bold text-soil-900 mb-2">{selectedNeighborhood.name}</h2>
                                    {/*<p className="text-xl sm:text-2xl font-semibold text-highland-500 mb-2">{selectedNeighborhood.price_range || 'N/A'}</p>*/}
                                    <p className="text-xl sm:text-2xl font-semibold text-highland-500 mb-2">{selectedNeighborhood.city || 'N/A'}</p>
                                    <div className="grid gap-6">
                                        {Object.entries(parseMarkdownDescription(selectedNeighborhood.description)).map(([sectionName, content]) => {
                                            if (sectionName === 'title') return null;
                                            return (
                                                <div key={sectionName} className="border-l-4 border-highland-500 pl-4 py-2">
                                                    <div className="flex items-center gap-2 mb-3">
                                                        {getSectionIcon(sectionName)}
                                                        <h3 className="text-xl font-semibold text-soil-900">{sectionName}</h3>
                                                    </div>
                                                    <p className="text-soil-600 leading-relaxed">{content}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    {selectedNeighborhood.trending && (
                                        <div
                                            className="bg-murram-100 text-murram-600 px-3 py-1 rounded-full text-sm font-medium">
                                            🔥 Trending
                                        </div>
                                    )}
                                    {selectedNeighborhood.featured && (
                                        <div
                                            className="bg-dawn-100 text-dawn-600 px-3 py-1 rounded-full text-sm font-medium">
                                            ⭐ Featured
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div
                                className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6 sm:mt-8 pt-6 border-t border-soil-100">
                                <button
                                    className="flex-1 bg-highland-500 text-white py-3 rounded-lg hover:bg-highland-600 transition-colors font-medium">
                                    <div className="flex items-center justify-center gap-2">
                                        <Phone className="w-4 h-4"/>
                                        <span className="text-sm sm:text-base">Contact Agent</span>
                                    </div>
                                </button>
                                <button
                                    className="flex-1 bg-dawn-500 text-soil-900 py-3 rounded-lg hover:bg-dawn-600 transition-colors font-medium">
                                    <div className="flex items-center justify-center gap-2">
                                        <Calendar className="w-4 h-4"/>
                                        <span className="text-sm sm:text-base">Schedule Tour</span>
                                    </div>
                                </button>
                                <button
                                    onClick={() => handleShare(selectedNeighborhood)}
                                    className="sm:flex-none px-6 py-3 border border-soil-100 rounded-lg hover:bg-soil-50 transition-colors flex items-center justify-center"
                                >
                                    <Share2 className="w-4 h-4"/>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NeighborhoodsPage;